import { useState, useEffect } from 'react';
import { Header } from '../components/layout/Header';

const API_BASE = '/api';

interface Graph {
  id: string;
  name: string;
  description: string;
  node_count: number;
  relationship_count: number;
  node_types: number;
  relationship_types: number;
  storage_mb: number;
  status: string;
  queries_per_second: number;
  avg_query_time_ms: number;
  cache_hit_rate: number;
  created_date: string;
}

interface Statistics {
  total_graphs: number;
  active_graphs: number;
  total_nodes: number;
  total_relationships: number;
  total_storage_mb: number;
  total_qps: number;
  avg_query_time_ms: number;
  avg_cache_hit_rate: number;
  node_types: number;
  relationship_types: number;
}

export default function GraphDatabasePage() {
  const [graphs, setGraphs] = useState<Graph[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [graphsRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/graphdb/graphs`),
        fetch(`${API_BASE}/graphdb/statistics`)
      ]);
      setGraphs(await graphsRes.json());
      setStatistics(await statsRes.json());
    } catch (error) {
      console.error('Error fetching graph database data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Graph Database</h1>
          <p className="mt-2 text-gray-600">Manage graph databases, nodes, and relationships</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Graphs</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_graphs}</p>
              <p className="text-sm text-green-600">{statistics.active_graphs} active</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Nodes</p>
              <p className="text-2xl font-bold text-blue-600">{formatNumber(statistics.total_nodes)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Relationships</p>
              <p className="text-2xl font-bold text-purple-600">{formatNumber(statistics.total_relationships)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total QPS</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(statistics.total_qps)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Cache Hit Rate</p>
              <p className="text-2xl font-bold text-green-600">{statistics.avg_cache_hit_rate}%</p>
            </div>
          </div>
        )}

        {/* Graphs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {graphs.map((graph) => (
            <div key={graph.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{graph.name}</h3>
                  <p className="text-sm text-gray-500">{graph.description}</p>
                </div>
                <span className={`px-3 py-1 text-xs rounded-full ${
                  graph.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {graph.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{formatNumber(graph.node_count)}</p>
                  <p className="text-xs text-blue-700">Nodes</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-purple-600">{formatNumber(graph.relationship_count)}</p>
                  <p className="text-xs text-purple-700">Relationships</p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Node Types</span>
                  <span className="font-medium">{graph.node_types}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Relationship Types</span>
                  <span className="font-medium">{graph.relationship_types}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Storage</span>
                  <span className="font-medium">{(graph.storage_mb / 1024).toFixed(1)} GB</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Queries/sec</span>
                  <span className="font-medium">{formatNumber(graph.queries_per_second)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Avg Query Time</span>
                  <span className="font-medium">{graph.avg_query_time_ms}ms</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Cache Hit Rate</span>
                  <span className="font-medium text-green-600">{graph.cache_hit_rate}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
